import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartAccessibility } from '../../hooks/useChartAccessibility';
import { ChartPatternDefs } from '../accessibility/ChartPatternDefs';
import { AccessibleDataTable } from '../accessibility/AccessibleDataTable';

export interface AreaChartDataPoint {
  name: string;
  [key: string]: string | number;
}

export interface AreaConfig {
  dataKey: string;
  name?: string;
  color?: string;
  fillOpacity?: number;
  stackId?: string;
  type?: 'linear' | 'monotone' | 'step';
  /** Render a marker at each data point. Keeps sparse series (e.g. a single
   *  non-zero point) visible — a smoothed area with one datum draws nothing. */
  dot?: boolean;
}

export interface AreaChartProps {
  data: AreaChartDataPoint[];
  areas: AreaConfig[];
  height?: number | string;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  xAxisDataKey?: string;
  yAxisLabel?: string;
  gradient?: boolean;
  className?: string;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
];

export function AreaChart({
  data,
  areas,
  height = '100%',
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  xAxisDataKey = 'name',
  yAxisLabel,
  gradient = true,
  className = '',
}: AreaChartProps) {
  const a11y = useChartAccessibility();
  // Effective color per series — palette override in color-blind mode.
  const colorAt = (index: number) =>
    a11y.resolveColor(index, areas[index]?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]);
  // Pattern fills replace the gradient (a gradient can't double-encode shape).
  const usePatterns = a11y.usePatternFills;

  const tableColumns = [
    { key: xAxisDataKey, label: 'Name' },
    ...areas.map((a) => ({ key: a.dataKey, label: a.name || a.dataKey })),
  ];

  return (
    <div className={`w-full h-full flex flex-col ${className}`} style={typeof height === 'number' ? { height } : undefined}>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsAreaChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            {usePatterns && ChartPatternDefs({ colors: areas.map((_, i) => colorAt(i)) })}
            {gradient && !usePatterns && (
              <defs>
                {areas.map((area, index) => {
                  const color = colorAt(index);
                  return (
                    <linearGradient
                      key={`gradient-${area.dataKey}`}
                      id={`gradient-${area.dataKey}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  );
                })}
              </defs>
            )}
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={a11y.gridColor} />}
            <XAxis
              dataKey={xAxisDataKey}
              tick={{ fontSize: 12 }}
              stroke={a11y.axisColor}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke={a11y.axisColor}
              label={
                yAxisLabel
                  ? { value: yAxisLabel, angle: -90, position: 'insideLeft' }
                  : undefined
              }
            />
            {showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: a11y.tooltipBg,
                  border: `1px solid ${a11y.tooltipBorder}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  color: a11y.tooltipText,
                }}
                labelStyle={{ color: a11y.tooltipText }}
                itemStyle={{ color: a11y.tooltipText }}
              />
            )}
            {showLegend && <Legend />}
            {areas.map((area, index) => {
              const color = colorAt(index);
              return (
                <Area
                  key={area.dataKey}
                  type={area.type || 'monotone'}
                  dataKey={area.dataKey}
                  name={area.name || area.dataKey}
                  stroke={color}
                  fill={usePatterns ? `url(#pattern-${index})` : gradient ? `url(#gradient-${area.dataKey})` : color}
                  fillOpacity={area.fillOpacity || 0.6}
                  stackId={area.stackId}
                  dot={area.dot ? { r: 3, strokeWidth: 0, fill: color } : false}
                  activeDot={{ r: 5 }}
                  isAnimationActive={a11y.isAnimationActive}
                  animationDuration={a11y.animationDuration}
                />
              );
            })}
          </RechartsAreaChart>
        </ResponsiveContainer>
      </div>
      <AccessibleDataTable caption="Area chart data" columns={tableColumns} data={data} />
    </div>
  );
}
